import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError, authLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { SESSIONS, stageOf } from "@/data/bridgeData";
import { ArrowRight, Users, UserPlus, X, Check, Copy, Link2, KeyRound, UserX, UserCheck, MessageCircle, Star, FileDown } from "lucide-react";

function SessionPacks() {
  const packs = [
    { id: "intake", eyebrow: "Week 0", title: "Intake & baseline" },
    ...SESSIONS.map((s) => ({ id: String(s.id), eyebrow: `Session ${s.id} · Week ${s.week} · ${stageOf(s.stage).name}`, title: s.title })),
  ];
  return (
    <section className="mt-16">
      <div className="label-eyebrow mb-3">Offline packs</div>
      <h2 className="font-serif text-3xl font-light text-stone-900 mb-3">Session packs to send</h2>
      <p className="mb-6 max-w-2xl text-stone-600 leading-relaxed">
        Print-ready PDF workbooks — one per session, with its reflections and activities — for clients working offline.
        Send each pack as the journey progresses.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {packs.map((p) => (
          <Link
            key={p.id}
            data-testid={`pack-${p.id}`}
            to={`/coach/pack/${p.id}`}
            className="group rounded-sm border border-stone-200 bg-white p-5 transition-all duration-300 hover:border-brand-primary/40 hover:shadow-sm"
          >
            <div className="text-xs uppercase tracking-wider text-stone-400 mb-1.5">{p.eyebrow}</div>
            <div className="font-serif text-xl text-stone-900 mb-3">{p.title}</div>
            <span className="inline-flex items-center gap-1.5 text-sm text-brand-primary group-hover:gap-2.5 transition-all">
              <FileDown size={15} /> Download pack
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ShareLink({ url, label = "Copy link" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="rounded-sm border border-stone-200 bg-white p-3">
      <p data-testid="share-link-url" className="break-all text-xs text-stone-600 mb-3">{url}</p>
      <button data-testid="share-link-copy" onClick={copy}
        className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2 text-sm text-white hover:bg-brand-hover transition-colors">
        {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : label}
      </button>
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      <div data-testid={testid} className="relative z-10 w-full max-w-md rounded-sm border border-stone-200 bg-brand-linen p-8 animate-fade-up">
        <button onClick={onClose} className="absolute right-5 top-5 text-stone-400 hover:text-stone-900 transition-colors"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

function AddPersonModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "participant" });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/users", form);
      setCreated({ ...data.user, url: authLink(data.token, "invite") });
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not create the account.");
    } finally {
      setLoading(false);
    }
  };

  if (created) {
    return (
      <Modal onClose={onClose} testid="add-person-modal">
        <div className="mb-3 flex items-center gap-2 text-brand-sage">
          <Check size={18} /> <span className="font-serif text-2xl text-stone-900">Invite ready</span>
        </div>
        <p className="text-sm text-stone-600 mb-5">
          Send this link to <span className="text-stone-900">{created.name}</span>. They'll set their own password and land in their studio. The link works for 14 days.
        </p>
        <ShareLink url={created.url} label="Copy invite link" />
        <button onClick={onClose} className="mt-5 w-full rounded-sm bg-brand-primary px-4 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors">Done</button>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} testid="add-person-modal">
      <form onSubmit={submit}>
        <div className="label-eyebrow mb-2">New account</div>
        <h3 className="font-serif text-3xl text-stone-900 mb-6">Invite a person</h3>
        <div className="space-y-5">
          <div>
            <label className="text-sm text-stone-600">Full name</label>
            <input data-testid="person-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" placeholder="Katerina" />
          </div>
          <div>
            <label className="text-sm text-stone-600">Email</label>
            <input data-testid="person-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" placeholder="person@example.com" />
          </div>
          <div>
            <label className="text-sm text-stone-600">Role</label>
            <select data-testid="person-role" value={form.role} onChange={(e) => set("role", e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors">
              <option value="participant">Participant</option>
              <option value="coach">Coach</option>
            </select>
          </div>
        </div>
        {error && <p data-testid="add-person-error" className="mt-4 text-sm text-[#9E4A41]">{error}</p>}
        <div className="mt-7 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-sm border border-stone-300 px-4 py-2.5 text-sm text-stone-700 hover:border-stone-400 transition-colors">Cancel</button>
          <button data-testid="person-submit" type="submit" disabled={loading}
            className="rounded-sm bg-brand-primary px-5 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ParticipantCard({ p, onOpen, onLink, onToggleActive, msgCount = 0, shareCount = 0 }) {
  return (
    <div className={`rounded-sm border bg-white p-7 transition-all duration-300 ${p.active ? "border-stone-200" : "border-stone-200 opacity-70"}`}>
      <button data-testid={`participant-${p.id}`} onClick={() => onOpen(p)} className="group w-full text-left">
        <div className="flex items-start justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-parchment font-serif text-2xl text-brand-primary mb-4">
            {p.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex items-center gap-2">
            {msgCount > 0 && (
              <span data-testid={`badge-messages-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-primary px-2 py-0.5 text-[11px] text-white">
                <MessageCircle size={11} /> {msgCount}
              </span>
            )}
            {shareCount > 0 && (
              <span data-testid={`badge-shares-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-sage px-2 py-0.5 text-[11px] text-white">
                <Star size={11} /> {shareCount}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-serif text-2xl text-stone-900">{p.name}</h3>
          {p.pending && <span className="rounded-full bg-brand-accent/15 px-2 py-0.5 text-[11px] text-brand-accent">Invite pending</span>}
          {!p.active && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[11px] text-stone-600">Inactive</span>}
        </div>
        <p className="text-sm text-stone-500">{p.email}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-sm text-brand-primary group-hover:gap-2 transition-all">
          Open studio <ArrowRight size={14} />
        </span>
      </button>

      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 border-t border-stone-100 pt-4 text-xs">
        <button data-testid={`action-invite-${p.id}`} onClick={() => onLink(p, "invite")} className="inline-flex items-center gap-1.5 text-stone-500 hover:text-brand-primary transition-colors">
          <Link2 size={13} /> {p.pending ? "Invite link" : "New invite"}
        </button>
        <button data-testid={`action-reset-${p.id}`} onClick={() => onLink(p, "reset")} className="inline-flex items-center gap-1.5 text-stone-500 hover:text-brand-primary transition-colors">
          <KeyRound size={13} /> Reset password
        </button>
        {p.active ? (
          <button data-testid={`action-deactivate-${p.id}`} onClick={() => onToggleActive(p, false)} className="inline-flex items-center gap-1.5 text-stone-500 hover:text-[#9E4A41] transition-colors">
            <UserX size={13} /> Deactivate
          </button>
        ) : (
          <button data-testid={`action-activate-${p.id}`} onClick={() => onToggleActive(p, true)} className="inline-flex items-center gap-1.5 text-stone-500 hover:text-brand-sage transition-colors">
            <UserCheck size={13} /> Reactivate
          </button>
        )}
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const { setActiveParticipant } = useAuth();
  const navigate = useNavigate();
  const notif = useNotifications();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [linkModal, setLinkModal] = useState(null);

  const load = () => {
    api.get("/participants")
      .then((res) => setParticipants(res.data || []))
      .catch((e) => { console.error("Failed to load participants:", e); setParticipants([]); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // mount-only: api is a stable singleton, setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (p) => { setActiveParticipant(p); navigate("/coach/participant"); };

  const makeLink = async (p, type) => {
    try {
      const ep = type === "reset" ? `/users/${p.id}/reset` : `/users/${p.id}/invite`;
      const { data } = await api.post(ep);
      setLinkModal({
        title: type === "reset" ? "Password reset link" : "Invite link",
        subtitle: type === "reset"
          ? `Send this to ${p.name} so they can set a new password (valid 2 days).`
          : `Send this to ${p.name} to set their password and join (valid 14 days).`,
        url: authLink(data.token, type),
      });
    } catch (e) {
      console.error("Failed to create link:", e);
    }
  };

  const toggleActive = async (p, active) => {
    try {
      await api.patch(`/users/${p.id}`, { active });
      load();
    } catch (e) {
      console.error("Failed to update participant:", e);
    }
  };

  const renderContent = () => {
    if (loading) return <p className="text-stone-500">Loading…</p>;
    if (participants.length === 0) {
      return (
        <div className="rounded-sm border border-dashed border-stone-300 bg-white p-12 text-center">
          <Users className="mx-auto mb-3 text-stone-300" />
          <p className="text-stone-600 mb-4">No participants yet. Invite your first one to get started.</p>
          <button data-testid="empty-add-participant" onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors">
            <UserPlus size={15} /> Invite participant
          </button>
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {participants.map((p) => (
          <ParticipantCard
            key={p.id}
            p={p}
            onOpen={open}
            onLink={makeLink}
            onToggleActive={toggleActive}
            msgCount={notif?.messages?.[p.id] || 0}
            shareCount={notif?.shares?.[p.id] || 0}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="label-eyebrow mb-3">Director's chair</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-3">Your participants</h1>
          <p className="max-w-2xl text-stone-600 leading-relaxed">
            Invite people, share their secure link, and open a studio to review reflections,
            track milestones, and capture coaching notes.
          </p>
        </div>
        <button data-testid="add-participant-btn" onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover">
          <UserPlus size={16} /> Invite person
        </button>
      </div>

      {renderContent()}

      <SessionPacks />

      {showAdd && <AddPersonModal onClose={() => setShowAdd(false)} onCreated={load} />}

      {linkModal && (
        <Modal onClose={() => setLinkModal(null)} testid="link-modal">
          <div className="label-eyebrow mb-2">Share securely</div>
          <h3 className="font-serif text-3xl text-stone-900 mb-2">{linkModal.title}</h3>
          <p className="text-sm text-stone-600 mb-5">{linkModal.subtitle}</p>
          <ShareLink url={linkModal.url} label="Copy link" />
          <button onClick={() => setLinkModal(null)} className="mt-5 w-full rounded-sm bg-brand-primary px-4 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors">Done</button>
        </Modal>
      )}
    </Layout>
  );
}
